import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { OnboardingStatus, type OnboardingSubmissionDto } from '@ciimar/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useT } from '../lib/i18n';
import { STATUS_CLASS, useStatusLabels, useSourceLabels, useFormatDate } from './status';

export function SubmissionDetail() {
  const t = useT();
  const statusLabels = useStatusLabels();
  const sourceLabels = useSourceLabels();
  const formatDate = useFormatDate();
  const { id } = useParams<{ id: string }>();
  const { isManager } = useAuth();
  const [item, setItem] = useState<OnboardingSubmissionDto | null>(null);
  const [fieldLabels, setFieldLabels] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [reference, setReference] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      setItem(await api.getSubmission(id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // Map field keys → labels so the extra answers read nicely in review.
    api
      .onboardingFields()
      .then((defs) => setFieldLabels(Object.fromEntries(defs.map((d) => [d.key, d.label]))))
      .catch(() => setFieldLabels({}));
  }, []);

  async function run(action: () => Promise<OnboardingSubmissionDto>) {
    setBusy(true);
    setError(null);
    try {
      setItem(await action());
      setNote('');
      setReference('');
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="muted">{t('onboarding.detail.loading')}</p>;
  if (!item) return <p className="alert error">{error ?? t('onboarding.detail.notFound')}</p>;

  const canReview =
    item.status === OnboardingStatus.PendingReview ||
    item.status === OnboardingStatus.ChangesRequested;

  return (
    <section className="detail">
      <Link to="/" className="back">
        ← {t('onboarding.detail.back')}
      </Link>
      <div className="page-head">
        <h2>{item.fullName}</h2>
        <span className={STATUS_CLASS[item.status]}>{statusLabels[item.status]}</span>
        <div className="spacer" />
        {isManager && !editing && (
          <button type="button" className="link-button" onClick={() => setEditing(true)}>
            {t('onboarding.detail.edit')}
          </button>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}

      {editing ? (
        <EditForm item={item} busy={busy} onCancel={() => setEditing(false)} onSave={run} />
      ) : (
        <dl className="fields">
          <Field label={t('onboarding.detail.fields.email')} value={item.email} />
          <Field label={t('onboarding.detail.fields.phone')} value={item.phone} />
          <Field label={t('onboarding.detail.fields.jobTitle')} value={item.jobTitle} />
          <Field label={t('onboarding.detail.fields.department')} value={item.department} />
          <Field label={t('onboarding.detail.fields.researchGroup')} value={item.researchGroup} />
          <Field label={t('onboarding.detail.fields.startDate')} value={item.startDate} />
          <Field label={t('onboarding.detail.fields.source')} value={sourceLabels[item.source]} />
          <Field label={t('onboarding.detail.fields.requestedBy')} value={item.requestedByName} />
          <Field
            label={t('onboarding.detail.fields.received')}
            value={formatDate(item.createdAt)}
          />
          <Field label={t('onboarding.detail.fields.reviewedBy')} value={item.reviewedBy} />
          <Field label={t('onboarding.detail.fields.reviewNote')} value={item.reviewNote} />
          <Field
            label={t('onboarding.detail.fields.sistradeReference')}
            value={item.sistradeReference}
          />
        </dl>
      )}

      {!editing && item.extraFields && Object.keys(item.extraFields).length > 0 && (
        <div className="notes">
          <h3>{t('onboarding.detail.extraInfo')}</h3>
          <dl className="fields">
            {Object.entries(item.extraFields).map(([key, value]) => (
              <Field key={key} label={fieldLabels[key] ?? key} value={value} />
            ))}
          </dl>
        </div>
      )}

      {!editing && item.notes && (
        <div className="notes">
          <h3>{t('onboarding.detail.notes')}</h3>
          <p>{item.notes}</p>
        </div>
      )}

      {isManager && !editing && (
        <div className="actions">
          <h3>{t('onboarding.detail.actions')}</h3>
          {canReview && (
            <>
              <textarea
                placeholder={t('onboarding.detail.notePlaceholder')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
              />
              <div className="buttons">
                <button
                  className="approve"
                  disabled={busy}
                  onClick={() => run(() => api.approve(item.id, note || undefined))}
                >
                  {t('onboarding.detail.approve')}
                </button>
                <button
                  className="changes"
                  disabled={busy || !note.trim()}
                  title={!note.trim() ? t('onboarding.detail.requestChangesHint') : undefined}
                  onClick={() => run(() => api.requestChanges(item.id, note))}
                >
                  {t('onboarding.detail.requestChanges')}
                </button>
                <button
                  className="reject"
                  disabled={busy}
                  onClick={() => run(() => api.reject(item.id, note || undefined))}
                >
                  {t('onboarding.detail.reject')}
                </button>
              </div>
            </>
          )}

          {item.status === OnboardingStatus.Approved && (
            <>
              <p className="muted">{t('onboarding.detail.launchHelp')}</p>
              <label>
                {t('onboarding.detail.sistradeReferenceOptional')}
                <input value={reference} onChange={(e) => setReference(e.target.value)} />
              </label>
              <div className="buttons">
                <button
                  className="sync"
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      api.markLaunched(item.id, { sistradeReference: reference || undefined }),
                    )
                  }
                >
                  {t('onboarding.detail.markLaunched')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function EditForm({
  item,
  busy,
  onCancel,
  onSave,
}: {
  item: OnboardingSubmissionDto;
  busy: boolean;
  onCancel: () => void;
  onSave: (action: () => Promise<OnboardingSubmissionDto>) => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState({
    fullName: item.fullName,
    email: item.email,
    phone: item.phone ?? '',
    jobTitle: item.jobTitle ?? '',
    department: item.department ?? '',
    researchGroup: item.researchGroup ?? '',
    startDate: item.startDate ?? '',
    notes: item.notes ?? '',
  });

  function set<K extends keyof typeof draft>(key: K, value: string) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(() => api.updateSubmission(item.id, draft));
      }}
    >
      <h3>{t('onboarding.detail.editForm.title')}</h3>
      <label>
        {t('onboarding.detail.editForm.fullName')}
        <input required value={draft.fullName} onChange={(e) => set('fullName', e.target.value)} />
      </label>
      <label>
        {t('onboarding.detail.editForm.email')}
        <input
          required
          type="email"
          value={draft.email}
          onChange={(e) => set('email', e.target.value)}
        />
      </label>
      <div className="row">
        <label>
          {t('onboarding.detail.editForm.phone')}
          <input value={draft.phone} onChange={(e) => set('phone', e.target.value)} />
        </label>
        <label>
          {t('onboarding.detail.editForm.startDate')}
          <input
            type="date"
            value={draft.startDate}
            onChange={(e) => set('startDate', e.target.value)}
          />
        </label>
      </div>
      <div className="row">
        <label>
          {t('onboarding.detail.editForm.jobTitle')}
          <input value={draft.jobTitle} onChange={(e) => set('jobTitle', e.target.value)} />
        </label>
        <label>
          {t('onboarding.detail.editForm.department')}
          <input value={draft.department} onChange={(e) => set('department', e.target.value)} />
        </label>
      </div>
      <label>
        {t('onboarding.detail.editForm.researchGroup')}
        <input value={draft.researchGroup} onChange={(e) => set('researchGroup', e.target.value)} />
      </label>
      <label>
        {t('onboarding.detail.editForm.notes')}
        <textarea rows={3} value={draft.notes} onChange={(e) => set('notes', e.target.value)} />
      </label>
      <div className="actions-row">
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          {t('onboarding.detail.editForm.cancel')}
        </button>
        <button type="submit" disabled={busy}>
          {busy ? t('onboarding.detail.editForm.saving') : t('onboarding.detail.editForm.save')}
        </button>
      </div>
    </form>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="field">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}
